import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MdArrowBack, MdSend } from "react-icons/md";
import { useAppLanguage } from "hooks/useAppLanguage";
import AdminDataTable, { AdminDataTableElement } from "components/ui/AdminDataTable";
import {
  DATA_TABLE_BODY_ROW,
  DATA_TABLE_HEADER_ROW,
  dataTableBodyCellClass,
  dataTableHeaderCellClass,
} from "lib/dataTableClasses";
import {
  UI_CARD,
  UI_MUTED_TEXT,
  UI_PAGE_TITLE,
  UI_PRIMARY_BTN,
  UI_SECONDARY_BTN,
} from "lib/uiClasses";
import { AdminNotification, notificationService } from "services/notificationService";

const RECIPIENT_STATUS_STYLE: Record<string, string> = {
  sent: "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300",
  read: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  failed: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300",
  pending: "bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-slate-300",
};

export default function NotificationDetailsPage() {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const navigate = useNavigate();
  const params = useParams();
  const id = Number(params.notificationId);

  const [item, setItem] = React.useState<AdminNotification | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [sending, setSending] = React.useState(false);

  const reload = React.useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await notificationService.get(id);
      setItem(data);
    } catch {
      setError(isArabic ? "تعذّر تحميل الإشعار" : "Could not load notification");
    } finally {
      setLoading(false);
    }
  }, [id, isArabic]);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  const handleSend = async () => {
    if (!id) return;
    setSending(true);
    try {
      await notificationService.send(id);
      await reload();
    } catch {
      // interceptor toasts the error
    } finally {
      setSending(false);
    }
  };

  const recipients = item?.recipients ?? [];
  const canSend = item && (item.status === "draft" || item.status === "scheduled" || item.status === "failed");

  return (
    <div className="mt-4 grid gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          className={`${UI_SECONDARY_BTN} inline-flex items-center gap-1`}
          onClick={() => navigate("/admin/notifications")}
        >
          <MdArrowBack className={`h-5 w-5 ${isArabic ? "rotate-180" : ""}`} />
          {isArabic ? "رجوع" : "Back"}
        </button>
        {canSend && (
          <button
            type="button"
            className={`${UI_PRIMARY_BTN} inline-flex items-center gap-1`}
            onClick={handleSend}
            disabled={sending}
          >
            <MdSend className="h-5 w-5" />
            {sending ? (isArabic ? "جارٍ الإرسال..." : "Sending...") : isArabic ? "إرسال الآن" : "Send now"}
          </button>
        )}
      </div>

      {loading ? (
        <div className={`${UI_CARD} p-6 ${UI_MUTED_TEXT}`}>{isArabic ? "جارٍ التحميل..." : "Loading..."}</div>
      ) : error || !item ? (
        <div className={`${UI_CARD} p-6 text-red-600`}>{error ?? (isArabic ? "غير موجود" : "Not found")}</div>
      ) : (
        <>
          <div className={`${UI_CARD} p-5`}>
            <h1 className={UI_PAGE_TITLE}>{item.title}</h1>
            <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700 dark:text-slate-200">{item.body}</p>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label={isArabic ? "الحالة" : "Status"} value={item.status} />
              <Stat label={isArabic ? "المستلمون" : "Recipients"} value={String(item.recipients_count)} />
              <Stat label={isArabic ? "نجح" : "Sent"} value={String(item.sent_count)} />
              <Stat label={isArabic ? "فشل" : "Failed"} value={String(item.failed_count)} />
            </div>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Stat label={isArabic ? "نوع الوجهة" : "Target type"} value={item.target_type ?? "none"} />
              <Stat
                label={isArabic ? "بيانات الوجهة" : "Target data"}
                value={item.target_data ? JSON.stringify(item.target_data) : "—"}
              />
            </div>
          </div>

          <div>
            <h2 className="mb-2 text-base font-bold text-navy-700 dark:text-slate-50">
              {isArabic ? "المستلمون" : "Recipients"}
            </h2>
            <AdminDataTable
              isArabic={isArabic}
              empty={recipients.length === 0}
              emptyTitle={isArabic ? "لا يوجد مستلمون" : "No recipients"}
            >
              <AdminDataTableElement isArabic={isArabic} minWidth="640px">
                <thead>
                  <tr className={DATA_TABLE_HEADER_ROW}>
                    <th className={dataTableHeaderCellClass(isArabic, false)}>
                      {isArabic ? "الاسم" : "Name"}
                    </th>
                    <th className={dataTableHeaderCellClass(isArabic, false)}>
                      {isArabic ? "النوع" : "Type"}
                    </th>
                    <th className={dataTableHeaderCellClass(isArabic, false, { compact: true })}>
                      {isArabic ? "الحالة" : "Status"}
                    </th>
                    <th className={dataTableHeaderCellClass(isArabic, false)}>
                      {isArabic ? "قُرئ" : "Read"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {recipients.map((r) => (
                    <tr key={r.id} className={DATA_TABLE_BODY_ROW}>
                      <td className={dataTableBodyCellClass(isArabic)}>
                        {r.recipient_name ?? `#${r.recipient_id}`}
                      </td>
                      <td className={dataTableBodyCellClass(isArabic)}>{r.recipient_type}</td>
                      <td className={dataTableBodyCellClass(isArabic, { compact: true })}>
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${
                            RECIPIENT_STATUS_STYLE[r.status] ?? RECIPIENT_STATUS_STYLE.pending
                          }`}
                        >
                          {r.status}
                        </span>
                      </td>
                      <td className={dataTableBodyCellClass(isArabic)}>{r.read_at ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </AdminDataTableElement>
            </AdminDataTable>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-gray-200 px-3 py-2 dark:border-white/[0.08]">
      <div className="text-xs font-semibold uppercase text-gray-500 dark:text-slate-400">{label}</div>
      <div className="mt-0.5 truncate text-sm font-bold text-navy-700 dark:text-white" title={value}>
        {value}
      </div>
    </div>
  );
}
